/**
 * Org API Keys API Client
 *
 * API client for org-level API key management endpoints.
 * These endpoints live on gw-functions (api.{env}.generalwisdom.com),
 * NOT on gw-platform-datasets.
 *
 * GW-1779, GW-2008
 */

import { assertOrgId } from '@/types/orgId';

import { BaseApiClient } from './baseApiClient';

import type { GenerateOrgApiKeyResponse, OrgApiKeyInfo } from '@gw/ui/types';

class OrgApiKeysApiClient extends BaseApiClient {
  constructor() {
    // Org API key endpoints live on gw-functions (apiBaseUrl), not gw-datasets-api.
    // No baseURLFactory needed — BaseApiClient defaults to getConfig().apiBaseUrl.
    super();
  }

  /**
   * Generate a new org-level API key
   * POST /v1/organizations/{orgId}/api-key
   */
  async generateOrgApiKey(orgId: string): Promise<GenerateOrgApiKeyResponse> {
    assertOrgId(orgId);
    return this.post<GenerateOrgApiKeyResponse>(`/organizations/${orgId}/api-key`);
  }

  /**
   * Get org-level API key metadata
   * GET /v1/organizations/{orgId}/api-key
   *
   * Returns metadata (prefix, dates) but NOT the full key value.
   */
  async getOrgApiKey(orgId: string): Promise<OrgApiKeyInfo> {
    assertOrgId(orgId);
    return this.get<OrgApiKeyInfo>(`/organizations/${orgId}/api-key`);
  }

  /**
   * Revoke an org-level API key
   * DELETE /v1/organizations/{orgId}/api-key/{keyId}
   */
  async revokeOrgApiKey(orgId: string, keyId: string): Promise<void> {
    assertOrgId(orgId);
    return this.delete<void>(`/organizations/${orgId}/api-key/${keyId}`);
  }

  /**
   * Regenerate an org-level API key
   * POST /v1/organizations/{orgId}/api-key/regenerate
   */
  async regenerateOrgApiKey(orgId: string): Promise<GenerateOrgApiKeyResponse> {
    assertOrgId(orgId);
    return this.post<GenerateOrgApiKeyResponse>(`/organizations/${orgId}/api-key/regenerate`);
  }

  /**
   * Reveal the full org-level API key value
   * GET /v1/organizations/{orgId}/api-key/reveal
   */
  async revealOrgApiKey(
    orgId: string
  ): Promise<{ keyId: string; keyValue: string; keyPrefix: string }> {
    assertOrgId(orgId);
    return this.get<{ keyId: string; keyValue: string; keyPrefix: string }>(
      `/organizations/${orgId}/api-key/reveal`
    );
  }
}

export const orgApiKeysApi = new OrgApiKeysApiClient();
